import { IconButton } from '@affine/component';
import { AffineErrorBoundary } from '@affine/core/components/affine/affine-error-boundary';
import { useI18n } from '@affine/i18n';
import { RightSidebarIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { Suspense, useCallback } from 'react';
import { Outlet } from 'react-router-dom';

import { AFFINE_RIGHT_SIDEBAR_SHORTCUT } from '../../../commands/affine-keybindings';
import { ViewService } from '../services/view';
import { WorkbenchService } from '../services/workbench';
import * as styles from './route-container.css';
import { useViewPosition } from './use-view-position';
import {
  ViewBody,
  ViewBodyTarget,
  ViewHeader,
  ViewHeaderTarget,
} from './view-islands';
import { ViewTitle } from './view-meta';

export interface Props {
  route: {
    Component: React.ComponentType;
  };
}

const ToggleButton = ({
  onToggle,
  className,
  show,
}: {
  onToggle?: () => void;
  className: string;
  show: boolean;
}) => {
  const t = useI18n();
  const tooltip = show
    ? t['com.streamlain.cmdk.affine.right-sidebar.expand']()
    : t['com.streamlain.cmdk.affine.right-sidebar.collapse']();

  return (
    <IconButton
      size="24"
      onClick={onToggle}
      className={className}
      data-show={show}
      data-testid="right-sidebar-toggle"
      aria-hidden={!show}
      aria-label={tooltip}
      tabIndex={show ? undefined : -1}
      tooltip={tooltip}
      tooltipShortcut={AFFINE_RIGHT_SIDEBAR_SHORTCUT}
      tooltipOptions={{ side: 'bottom' }}
    >
      <RightSidebarIcon />
    </IconButton>
  );
};

const RoutePendingFallback = () => {
  const t = useI18n();
  const label = t['Loading']();

  return (
    <>
      <ViewTitle title={label} />
      <ViewHeader />
      <ViewBody>
        <div className={styles.routePending} role="status" aria-live="polite">
          <span className={styles.routePendingText}>{label}</span>
        </div>
      </ViewBody>
    </>
  );
};

export const RouteContainer = () => {
  const viewPosition = useViewPosition();
  const workbench = useService(WorkbenchService).workbench;
  const view = useService(ViewService).view;
  const sidebarOpen = useLiveData(workbench.sidebarOpen$);
  const handleToggleSidebar = useCallback(() => {
    workbench.toggleSidebar();
  }, [workbench]);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <ViewHeaderTarget
          viewId={view.id}
          className={styles.viewHeaderContainer}
        />
        {!BUILD_CONFIG.isElectron && viewPosition.isLast && (
          <ToggleButton
            show={!sidebarOpen}
            className={styles.rightSidebarButton}
            onToggle={handleToggleSidebar}
          />
        )}
      </div>

      <AffineErrorBoundary>
        <Suspense fallback={<RoutePendingFallback />}>
          <Outlet />
        </Suspense>
      </AffineErrorBoundary>
      <ViewBodyTarget viewId={view.id} className={styles.viewBodyContainer} />
    </div>
  );
};
